import type { DisciplineSnapshot, StudyBlock, StudyDay, StudyPlan } from "./types";

const ALL_DISCIPLINES = [
  "portugues",
  "matematica",
  "biologia",
  "quimica",
  "fisica",
  "ingles",
  "historia",
  "geografia",
] as const;

const DISCIPLINE_LABELS: Record<string, string> = {
  portugues: "Português",
  matematica: "Matemática",
  biologia: "Biologia",
  quimica: "Química",
  fisica: "Física",
  ingles: "Inglês",
  historia: "História",
  geografia: "Geografia",
};

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function disciplineLabel(slug: string): string {
  return DISCIPLINE_LABELS[slug] ?? slug;
}

/**
 * Próxima segunda-feira a partir de "hoje" (inclusive se hoje for segunda).
 */
export function currentWeekStart(now = new Date()): string {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay(); // 0 = dom, 1 = seg
  const diff = day === 0 ? -6 : 1 - day; // volta pra segunda da semana atual
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

interface BuildInput {
  /** Snapshots por disciplina vindos do diagnóstico mais recente. */
  snapshots: DisciplineSnapshot[];
  weekly_hours_target: number;
  exam_date: string | null;
  based_on_session_id: string | null;
}

/**
 * Gera plano semanal v1.
 *
 * Heurística:
 *   - 5 dias úteis (seg-sex): 2-3 blocos de estudo cada.
 *   - sábado: bloco de revisão geral + simulado curto.
 *   - domingo: descanso.
 *   - 1 bloco/semana reservado pra redação (ter ou qui).
 *   - 1 bloco/semana reservado pra discursiva (qua).
 *   - Demais blocos vão pra disciplinas inversamente proporcionais à acurácia.
 *
 * Quando não há diagnóstico (snapshots vazio), assume distribuição uniforme.
 */
export function buildStudyPlan(input: BuildInput): StudyPlan {
  const weekStart = currentWeekStart();
  const totalMinutes = Math.round(input.weekly_hours_target * 60);

  // Determinar blocos de estudo a distribuir.
  // 1 redação (60 min) + 1 discursiva (45 min) + 1 revisão (45 min) já fixos.
  const RESERVED_MIN = 60 + 45 + 45;
  const studyMin = Math.max(60, totalMinutes - RESERVED_MIN);

  // Cada bloco de estudo = ~50 min. Mínimo 4, máximo 12.
  const studyBlockCount = Math.min(12, Math.max(4, Math.round(studyMin / 50)));
  const studyBlockSize = Math.round(studyMin / studyBlockCount);

  const allocations = allocateByWeakness(input.snapshots, studyBlockCount);

  const days: StudyDay[] = [];
  const today = new Date(weekStart);

  // Distribuição padrão por dia (índices 0..6: seg=0 ... dom=6).
  // Mapeia que tipo de bloco cai em qual dia.
  // Os "X" são placeholders pra blocos de estudo distribuídos por acurácia.
  const layout: Array<Array<{ kind: StudyBlock["kind"]; minutes?: number }>> = [
    [{ kind: "study" }, { kind: "study" }], // seg
    [{ kind: "study" }, { kind: "essay", minutes: 60 }], // ter — redação
    [{ kind: "study" }, { kind: "discursive", minutes: 45 }], // qua
    [{ kind: "study" }, { kind: "study" }], // qui
    [{ kind: "study" }, { kind: "study" }], // sex
    [{ kind: "review", minutes: 45 }, { kind: "simulation", minutes: 50 }], // sáb
    [], // dom — descanso
  ];

  let pickIdx = 0;
  const queue = expandQueue(allocations);

  for (let i = 0; i < 7; i++) {
    const dayDate = new Date(today);
    dayDate.setDate(today.getDate() + i);
    const dateIso = dayDate.toISOString().slice(0, 10);
    const blocks: StudyBlock[] = [];

    for (const slot of layout[i] ?? []) {
      if (slot.kind === "study") {
        const disc = queue[pickIdx % queue.length];
        pickIdx += 1;
        if (!disc) continue;
        blocks.push(buildStudyBlock(disc, studyBlockSize));
      } else if (slot.kind === "essay") {
        blocks.push({
          id: uuid(),
          kind: "essay",
          duration_min: slot.minutes ?? 60,
          reason: "Vunesp pede redação dissertativa-argumentativa sem proposta. Treinar 1/semana.",
          link: "/redacao",
        });
      } else if (slot.kind === "discursive") {
        // discursiva da disciplina mais fraca dentre as que caem em discursiva
        const discFocus = pickDiscursiveTarget(input.snapshots);
        blocks.push({
          id: uuid(),
          kind: "discursive",
          discipline: discFocus,
          duration_min: slot.minutes ?? 45,
          reason: `Discursiva de ${disciplineLabel(discFocus)} — área com menor acerto agora.`,
          link: "/discursivas",
        });
      } else if (slot.kind === "review") {
        blocks.push({
          id: uuid(),
          kind: "review",
          duration_min: slot.minutes ?? 45,
          reason: "Revisão SM-2 — fila de questões que você errou ou hesitou.",
          link: "/revisar",
        });
      } else if (slot.kind === "simulation") {
        blocks.push({
          id: uuid(),
          kind: "simulation",
          duration_min: slot.minutes ?? 50,
          reason: "Simulado curto: 10 questões objetivas cronometradas.",
          link: "/diagnostico/sessao",
        });
      }
    }

    blocks.length > 0 &&
      days.push({
        date: dateIso,
        weekday: dayDate.getDay(),
        blocks,
        total_min: blocks.reduce((acc, b) => acc + b.duration_min, 0),
      });
  }

  const notes: string[] = [];
  if (input.snapshots.length === 0) {
    notes.push("Sem diagnóstico ainda — plano usa distribuição equilibrada. Faça o diagnóstico para calibrar.");
  } else {
    const worst = [...input.snapshots].sort((a, b) => a.accuracy - b.accuracy).slice(0, 3);
    notes.push(
      `Priorizando ${worst.map((s) => disciplineLabel(s.discipline)).join(", ")} — menor acerto no diagnóstico.`,
    );
  }
  if (input.exam_date) {
    const days = Math.max(0, Math.round((new Date(input.exam_date).getTime() - Date.now()) / 86400000));
    notes.push(`${days} dias até a prova prevista.`);
  }

  return {
    week_start: weekStart,
    generated_at: new Date().toISOString(),
    weekly_hours_target: input.weekly_hours_target,
    days,
    marginal_gain_estimate: estimateMarginalGain(input.snapshots),
    based_on_session_id: input.based_on_session_id,
    notes,
  };
}

function buildStudyBlock(discipline: string, durationMin: number): StudyBlock {
  return {
    id: uuid(),
    kind: "study",
    discipline,
    duration_min: durationMin,
    reason: `Estudo focado em ${disciplineLabel(discipline)}.`,
    link: "/diagnostico/sessao",
  };
}

/**
 * Distribui N blocos entre disciplinas. Disciplinas com menor acurácia
 * recebem mais blocos. Sem diagnóstico → uniforme.
 */
function allocateByWeakness(
  snapshots: DisciplineSnapshot[],
  totalBlocks: number,
): Record<string, number> {
  const allocation: Record<string, number> = {};
  if (snapshots.length === 0) {
    // uniforme entre todas as 8
    const per = Math.floor(totalBlocks / ALL_DISCIPLINES.length);
    const extra = totalBlocks - per * ALL_DISCIPLINES.length;
    ALL_DISCIPLINES.forEach((d, i) => {
      allocation[d] = per + (i < extra ? 1 : 0);
    });
    return allocation;
  }
  // Pesos = 1 - accuracy, mínimo 0.1 pra não zerar nada.
  const weights = snapshots.map((s) => ({
    discipline: s.discipline,
    weight: Math.max(0.1, 1 - s.accuracy),
  }));
  const sum = weights.reduce((acc, w) => acc + w.weight, 0);
  let allocated = 0;
  for (const w of weights) {
    const share = Math.max(1, Math.round((w.weight / sum) * totalBlocks));
    allocation[w.discipline] = share;
    allocated += share;
  }
  // Garante que disciplinas não-snapshot ainda recebam 1 bloco se sobrar.
  for (const d of ALL_DISCIPLINES) {
    if (!(d in allocation) && allocated < totalBlocks) {
      allocation[d] = 1;
      allocated += 1;
    }
  }
  return allocation;
}

/** Vira o map em fila com a discipline repetida pelo seu allocation. */
function expandQueue(alloc: Record<string, number>): string[] {
  const queue: string[] = [];
  const entries = Object.entries(alloc).sort((a, b) => b[1] - a[1]);
  // round-robin pelo maior allocation: alterna disciplinas pra não amontoar tudo num dia
  const max = Math.max(...entries.map(([, n]) => n), 0);
  for (let r = 0; r < max; r++) {
    for (const [disc, n] of entries) {
      if (r < n) queue.push(disc);
    }
  }
  return queue;
}

function pickDiscursiveTarget(snapshots: DisciplineSnapshot[]): string {
  const DISC_DISCIPLINES = ["biologia", "quimica", "fisica", "matematica", "ingles"];
  if (snapshots.length === 0) return "biologia";
  const candidates = snapshots.filter((s) => DISC_DISCIPLINES.includes(s.discipline));
  if (candidates.length === 0) return "biologia";
  return candidates.sort((a, b) => a.accuracy - b.accuracy)[0]!.discipline;
}

function estimateMarginalGain(snapshots: DisciplineSnapshot[]): number {
  if (snapshots.length === 0) return 0;
  // Heurística simples: para cada disciplina, a margem é (1 - accuracy) * peso na prova.
  // Pesos aproximados Einstein: port 10, mat 10, bio 5, quim 5, fis 5, ing 5, hist 5, geo 5 = 50.
  const weights: Record<string, number> = {
    portugues: 10,
    matematica: 10,
    biologia: 5,
    quimica: 5,
    fisica: 5,
    ingles: 5,
    historia: 5,
    geografia: 5,
  };
  let gain = 0;
  for (const s of snapshots) {
    const w = weights[s.discipline] ?? 5;
    gain += (1 - s.accuracy) * w * 0.4; // 40% do gap é capturável em 1 semana
  }
  return Math.round(gain * 10) / 10;
}

export { disciplineLabel };
